/**
 * Recherche « à la frappe » — briques pures partagées par `<Autocomplete>`.
 *
 * Insensible à la casse ET aux accents (« Theatre » trouve « Théâtre des
 * Arts ») : au poste de régulation on tape vite, souvent sans accents.
 */

/** Minuscules, sans diacritiques, espaces de bord retirés. */
export function normalizeSearch(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** Rang d'une correspondance — plus petit = meilleur. */
export const MATCH_EXACT = 0;
export const MATCH_PREFIX = 1;
export const MATCH_SUBSTRING = 2;

/**
 * Meilleur rang de `query` (déjà normalisée) parmi `terms`, ou `null` si
 * aucun terme ne correspond. Une requête vide ne correspond à rien : c'est à
 * l'appelant de décider s'il affiche une liste par défaut.
 */
export function bestMatchRank(terms: ReadonlyArray<string | null | undefined>, query: string): number | null {
  if (!query) return null;
  let best: number | null = null;
  for (const raw of terms) {
    if (!raw) continue;
    const term = normalizeSearch(raw);
    let rank: number | null = null;
    if (term === query) rank = MATCH_EXACT;
    else if (term.startsWith(query)) rank = MATCH_PREFIX;
    else if (term.includes(query)) rank = MATCH_SUBSTRING;
    if (rank !== null && (best === null || rank < best)) best = rank;
    if (best === MATCH_EXACT) break;
  }
  return best;
}

/**
 * Filtre + classe `options` pour `rawQuery`. Tri stable : à rang égal, l'ordre
 * d'origine de `options` est conservé (l'appelant y met son tri métier).
 */
export function filterAndRank<T>(
  options: readonly T[],
  rawQuery: string,
  getSearchTerms: (option: T) => ReadonlyArray<string | null | undefined>,
): T[] {
  const query = normalizeSearch(rawQuery);
  if (!query) return [];
  const ranked: { option: T; rank: number; index: number }[] = [];
  options.forEach((option, index) => {
    const rank = bestMatchRank(getSearchTerms(option), query);
    if (rank !== null) ranked.push({ option, rank, index });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return ranked.map((r) => r.option);
}
